import React, { useState } from "react";
import { Mail, MapPin, Send, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Formspree: Replace YOUR_FORM_ID with your actual Formspree form ID
// Sign up free at https://formspree.io → Create form → Copy form ID
const FORMSPREE_URL = "https://formspree.io/f/xjgaboqv";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Bitte füllen Sie alle Felder aus.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(FORMSPREE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          _subject: form.subject,
          message: form.message,
          _replyto: form.email,
        }),
      });
      if (res.ok) {
        setSubmitted(true);
        toast.success("Nachricht erfolgreich gesendet!");
        setForm({ name: "", email: "", subject: "", message: "" });
      } else {
        throw new Error("Failed");
      }
    } catch {
      toast.error("Fehler beim Senden. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="contact-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-sm font-semibold tracking-wide uppercase text-[#FF3B30]" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Kontakt
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0F172A] mt-3 max-w-3xl" style={{ fontFamily: 'Plus Jakarta Sans' }} data-testid="contact-page-title">
            Kontaktieren Sie uns
          </h1>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed mt-6 max-w-2xl">
            Haben Sie Fragen oder möchten Sie mehr über unsere Dienstleistungen erfahren? Wir freuen uns auf Ihre Nachricht.
          </p>
        </div>
      </section>

      {/* Contact Form + Info */}
      <section className="py-24 md:py-32" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20">
            {/* Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-6">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-2xl font-semibold text-[#0F172A] mb-3" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                    Vielen Dank!
                  </h3>
                  <p className="text-slate-600 max-w-sm mb-8">
                    Ihre Nachricht wurde erfolgreich gesendet. Wir werden uns so schnell wie möglich bei Ihnen melden.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="text-[#FF3B30] font-medium text-sm hover:underline"
                    data-testid="contact-send-another"
                  >
                    Weitere Nachricht senden
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <label className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-2 block" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                        Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Ihr Name"
                        className="swiss-input"
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-2 block" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                        E-Mail
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="ihre@email.ch"
                        className="swiss-input"
                        data-testid="contact-input-email"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-2 block" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                      Betreff
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder="Betreff Ihrer Nachricht"
                      className="swiss-input"
                      data-testid="contact-input-subject"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-2 block" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                      Nachricht
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Ihre Nachricht..."
                      rows={5}
                      className="swiss-input resize-none"
                      data-testid="contact-input-message"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center gap-2 bg-[#FF3B30] text-white h-12 px-8 rounded-sm font-medium transition-all duration-300 hover:bg-[#E5352B] shadow-sm hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="contact-submit-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Wird gesendet...
                      </>
                    ) : (
                      <>
                        Nachricht senden
                        <Send size={16} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2" data-testid="contact-info">
              <div className="bg-[#F8FAFC] rounded-sm p-8 space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                    Kontaktinformationen
                  </h3>
                  <div className="space-y-4">
                    <a
                      href="mailto:servicepoint.huwiler89@gmail.com"
                      className="flex items-start gap-3 text-slate-600 hover:text-[#FF3B30] transition-colors group"
                      data-testid="contact-email-link"
                    >
                      <Mail size={18} className="mt-0.5 shrink-0" strokeWidth={1.5} />
                      <span className="text-sm">servicepoint.huwiler89@gmail.com</span>
                    </a>
                    <div className="flex items-start gap-3 text-slate-600">
                      <MapPin size={18} className="mt-0.5 shrink-0" strokeWidth={1.5} />
                      <div className="text-sm">
                        <p>Sellackerstrasse 3</p>
                        <p>6033 Buchrain</p>
                        <p>Schweiz</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-8">
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-3" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                    Inhaberin
                  </h3>
                  <p className="text-slate-600 text-sm">Sarah Huwiler</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
